import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { COMPANY } from "../data/content";

const NAV = [
  { to: "/", label: "Home", testid: "nav-link-home", end: true },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/investors", label: "Investors", testid: "nav-link-investors" },
  { to: "/sec-filings", label: "SEC Filings", testid: "nav-link-sec-filings" },
  { to: "/legacy-assets", label: "Legacy Assets", testid: "nav-link-legacy" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 bg-[#14110E]/95 backdrop-blur-md border-b border-[#3A2E22]"
      data-testid="site-header"
    >
      {/* Top strip — ticker */}
      <div className="hidden md:block border-b border-[#3A2E22]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between py-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[#A79E93]">
          <div className="flex items-center gap-6" data-testid="ticker-strip">
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F26A1B] animate-pulse" />
              <span className="text-white">OTCQB: {COMPANY.ticker}</span>
            </span>
            <span>SEC CIK {COMPANY.cik}</span>
            <span>FY End {COMPANY.fiscalYearEnd}</span>
          </div>
          <a
            href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=0001695473&type=&dateb=&owner=include&count=40"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-white hover:text-[#F26A1B] transition-colors"
            data-testid="header-edgar-link"
          >
            SEC EDGAR profile <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" aria-label="Trafalgar International Inc — Home" data-testid="brand-link">
          <Logo variant="dark" size={28} dataTestId="brand-logo-header" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `group text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-white font-semibold"
                    : "text-[#F5F2EB] hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="inline-flex flex-col items-start">
                  <span>{n.label}</span>
                  <span
                    aria-hidden
                    className={`block h-[2px] mt-1 transition-all ${
                      isActive
                        ? "w-full bg-[#F26A1B]"
                        : "w-0 bg-[#F26A1B] group-hover:w-full"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href="/sec-filings"
            className="inline-flex items-center gap-2 btn-gradient rounded-full text-sm font-semibold px-5 py-2.5 tracking-wide transition-colors"
            data-testid="header-cta-filings"
          >
            View SEC Filings <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden border-t border-[#3A2E22] bg-[#14110E]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`m-${n.testid}`}
                className={({ isActive }) =>
                  `py-3 border-b border-[#3A2E22] text-sm ${
                    isActive ? "text-[#F26A1B] font-semibold" : "text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href="/sec-filings"
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#F26A1B] text-white text-sm font-semibold px-5 py-3"
              data-testid="m-header-cta-filings"
            >
              View SEC Filings <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
